/**
 * @file TablePane.tsx
 * @description Screening queue with status badges and scores.
 * Renders cards on mobile and a table on desktop.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
    ChevronLeft,
    ChevronRight,
    Trash2,
    Clock,
    Loader2,
    Trash,
    AlertCircle,
    CheckCircle2,
    List,
    Eye,
    FileText
} from 'lucide-react';
import { AppButton } from './components';

export interface TablePaneItem {
    file: File;
    status: 'idle' | 'processing' | 'done' | 'error';
    stage?: 'text' | 'ocr';
    resultJson?: { fullname?: string } | null;
    score?: number;
    errorDetail?: string;
}

interface TablePaneProps {
    files: TablePaneItem[];
    onDetailClick: (item: TablePaneItem) => void;
    onRemoveFile: (index: number) => void;
    onRemoveAll: () => void;
    isAnalyzing: boolean;
    currentIndex: number | null;
    title: string;
}

export const TablePane: React.FC<TablePaneProps> = ({
    files,
    onDetailClick,
    onRemoveFile,
    onRemoveAll,
    isAnalyzing,
    currentIndex,
    title
}) => {
    /* --- STATE MANAGEMENT --- */
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [isMobile, setIsMobile] = useState(false);

    /* --- LOGIC: Handle Responsive View --- */
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    /* --- LOGIC: Pagination calculations --- */
    const totalPages = Math.ceil(files.length / pageSize);
    const safePage = Math.max(1, Math.min(page, totalPages || 1));
    const doneCount = useMemo(() => files.filter(f => f.status === 'done').length, [files]);

    /**
     * DERIVED DATA: visibleFiles
     * Memoized slice ensuring reactivity when parent state updates.
     */
    const visibleFiles = useMemo(() =>
        files.slice((safePage - 1) * pageSize, safePage * pageSize),
    [files, safePage, pageSize]);

    return (
        <section className="bg-white p-4 md:p-8 rounded-3xl border border-slate-200 shadow-lg fade-in w-full overflow-hidden text-left">

            {/* Header section with Meta info */}
            <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
                <div className="text-left">
                    <h3 className="text-xl font-black text-slate-900 leading-tight">{title}</h3>
                    <p className="text-sm text-slate-400 font-bold mt-1 uppercase tracking-wider">
                        Fronta: {files.length} | Hotovo: {doneCount}
                    </p>
                </div>
                {files.length > 0 && !isAnalyzing && (
                    <button
                        type="button"
                        onClick={onRemoveAll}
                        className="flex items-center gap-1.5 text-red-500 text-[10px] font-black hover:underline tracking-widest transition-all"
                    >
                        <Trash size={14} /> SMAZAT VŠE
                    </button>
                )}
            </div>

            {/* --- DATA DISPLAY: Responsive Table/Cards --- */}
            {isMobile ? (
                /* --- MOBILE CARD VIEW --- */
                <div className="space-y-4">
                    {visibleFiles.length > 0 ? visibleFiles.map((item, idx) => {
                        const globalIdx = (safePage - 1) * pageSize + idx;
                        const isActive = currentIndex === globalIdx;
                        const isError = item.status === 'error';
                        const isDone = item.status === 'done';

                        return (
                            <div key={`${item.file.name}-${globalIdx}`} className={`p-5 rounded-2xl border ${isActive ? 'bg-blue-50/50 border-blue-200 shadow-inner' : isError ? 'bg-red-50/40 border-red-200' : 'bg-white border-slate-100 shadow-sm'} transition-all`}>
                                <div className="flex justify-between items-start gap-4 mb-4">
                                    <div className="flex gap-3">
                                        <div className={`p-2 rounded-lg ${isDone ? 'bg-green-50 text-green-600' : isError ? 'bg-red-50 text-red-500' : 'bg-slate-50 text-slate-400'}`}>
                                            <FileText size={20} />
                                        </div>
                                        <div>
                                            <div className="font-bold text-slate-900 text-sm line-clamp-1">{item.file.name}</div>
                                            <div className={`text-[10px] font-bold uppercase tracking-wide mt-0.5 ${isDone ? 'text-green-600' : isActive ? 'text-blue-600' : isError ? 'text-red-500' : 'text-orange-500'}`}>
                                                {isDone ? item.resultJson?.fullname : isActive ? (item.stage === 'ocr' ? 'OCR skenu…' : 'zpracovávám…') : isError ? 'chyba' : 'čeká ve frontě'}
                                            </div>
                                        </div>
                                    </div>
                                    {isDone && (
                                        <div className="bg-slate-900 text-white px-3 py-1 rounded-lg font-black text-xs">
                                            {item.score}%
                                        </div>
                                    )}
                                </div>

                                {isError && item.errorDetail && (
                                    <div className="flex items-start gap-2 mb-4 text-[11px] font-semibold text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                                        <AlertCircle size={14} className="shrink-0 mt-0.5" />
                                        <span className="line-clamp-3">{item.errorDetail}</span>
                                    </div>
                                )}

                                <div className="flex items-center justify-between gap-2 border-t border-slate-50 pt-4">
                                    <div className="flex-1">
                                        {isActive ? (
                                            <div className="flex items-center gap-2 text-[10px] font-black text-blue-600 animate-pulse uppercase">
                                                <Loader2 size={12} className="animate-spin" /> Zpracovávám…
                                            </div>
                                        ) : isDone ? (
                                            <AppButton
                                                variant="primary"
                                                onClick={() => onDetailClick(item)}
                                                className="w-full py-2 text-[10px] font-black uppercase tracking-widest shadow-md"
                                            >
                                                <Eye size={12} /> Zobrazit detail
                                            </AppButton>
                                        ) : isError ? (
                                            <div className="text-[10px] font-black text-red-500 uppercase flex items-center gap-1.5">
                                                <AlertCircle size={12} /> Zpracování selhalo
                                            </div>
                                        ) : (
                                            <div className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1.5">
                                                <Clock size={12} /> Připraveno ke screeningu
                                            </div>
                                        )}
                                    </div>
                                    {!isActive && !isDone && (
                                        <button
                                            onClick={() => onRemoveFile(globalIdx)}
                                            className="p-2.5 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 transition-colors shadow-sm"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    }) : (
                        <div className="py-10 text-center text-slate-300 font-bold italic border-2 border-dashed border-slate-100 rounded-2xl uppercase text-xs tracking-widest">Fronta je prázdná</div>
                    )}
                </div>
            ) : (
                /* --- DESKTOP TABLE VIEW --- */
                <div className="w-full overflow-x-auto">
                    <table className="w-full border-collapse text-left">
                        <thead>
                            <tr className="border-b border-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                                <th className="py-4 px-2">Subjekt / Soubor</th>
                                <th className="py-4 px-2 text-center">Skóre</th>
                                <th className="py-4 px-2 hidden sm:table-cell">Stav analýzy</th>
                                <th className="py-4 px-2 text-right">Akce</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {visibleFiles.map((item, idx) => {
                                const globalIdx = (safePage - 1) * pageSize + idx;
                                const isActive = currentIndex === globalIdx;
                                const isError = item.status === 'error';
                                const isDone = item.status === 'done';

                                return (
                                    <tr key={`${item.file.name}-${globalIdx}`} className={`group transition-all ${isActive ? 'bg-blue-50/50' : isError ? 'bg-red-50/30' : 'hover:bg-slate-50/30'}`}>
                                        <td className="py-5 px-2">
                                            <div className="font-bold text-slate-900 text-base leading-tight">{item.file.name}</div>
                                            <div className={`text-[11px] font-bold mt-1 ${isDone ? 'text-green-600' : isActive ? 'text-blue-600' : isError ? 'text-red-500' : 'text-orange-500'}`}>
                                                {isDone ? item.resultJson?.fullname : isActive ? (item.stage === 'ocr' ? 'OCR skenu…' : 'zpracovávám…') : isError ? 'chyba zpracování' : 'čeká na screening'}
                                            </div>
                                            {isError && item.errorDetail && (
                                                <div className="text-[11px] font-semibold text-red-500/80 mt-1 max-w-md truncate" title={item.errorDetail}>
                                                    {item.errorDetail}
                                                </div>
                                            )}
                                        </td>
                                        <td className="py-5 px-2 text-center">
                                            {isDone ? (
                                                <div className="inline-block px-3 py-1 bg-slate-900 text-white rounded-lg font-black text-sm shadow-md">{item.score}%</div>
                                            ) : <span className="text-slate-200 font-bold">—</span>}
                                        </td>
                                        <td className="py-5 px-2 hidden sm:table-cell">
                                            {isDone ? (
                                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 text-green-700 font-black text-[10px] border border-green-100 shadow-sm"><CheckCircle2 size={12} /> hotovo</div>
                                            ) : isActive ? (
                                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 font-bold text-[10px] border border-blue-100 animate-pulse shadow-sm"><Loader2 size={12} className="animate-spin" /> zpracovávám…</div>
                                            ) : isError ? (
                                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 text-red-600 font-black text-[10px] border border-red-100 shadow-sm"><AlertCircle size={12} /> chyba</div>
                                            ) : (
                                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 text-orange-600 font-black text-[10px] border border-orange-200 shadow-sm"><Clock size={12} /> čeká ve frontě</div>
                                            )}
                                        </td>
                                        <td className="py-5 px-2 text-right">
                                            <div className="flex justify-end items-center gap-2">
                                                {isDone ? (
                                                    <AppButton variant="primary" onClick={() => onDetailClick(item)} className="py-1.5 px-4 text-[10px] font-black uppercase shadow-sm">
                                                        <Eye size={12} className="mr-1" /> Detail
                                                    </AppButton>
                                                ) : !isActive && (
                                                    <button onClick={() => onRemoveFile(globalIdx)} className={`p-2 transition-all ${isError ? 'text-red-400 hover:text-red-600' : 'text-slate-200 hover:text-red-500'} hover:scale-110`}><Trash2 size={18} /></button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {visibleFiles.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="py-12 text-center text-slate-300 font-bold italic uppercase text-xs tracking-widest">
                                        Fronta je prázdná – nahrajte dokument nebo přidejte vzorový scénář
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* --- PAGINATION FOOTER WITH SELECTOR --- */}
            <div className="mt-6 pt-4 border-t border-slate-50 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100 shadow-inner">
                        <List size={14} className="text-slate-400" />
                        <select
                            value={pageSize}
                            onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1); }}
                            className="bg-transparent font-bold text-slate-900 outline-none text-[11px] cursor-pointer"
                        >
                            {[5, 10, 20, 50].map(val => (
                                <option key={val} value={val}>{val} na stránku</option>
                            ))}
                        </select>
                    </div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                        Strana {safePage} z {totalPages || 1}
                    </span>
                </div>

                <div className="flex gap-2">
                    <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} className="p-2.5 rounded-xl border border-slate-200 text-slate-400 disabled:opacity-20 hover:bg-slate-50 transition-all active:scale-90 shadow-sm"><ChevronLeft size={16}/></button>
                    <button type="button" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages || totalPages === 0} className="p-2.5 rounded-xl border border-slate-200 text-slate-400 disabled:opacity-20 hover:bg-slate-50 transition-all active:scale-90 shadow-sm"><ChevronRight size={16}/></button>
                </div>
            </div>
        </section>
    );
};
